// JaKompassBentoTile — festgestellte Jahresabschluss-Kernwerte (Zahlen-Tab).
// Daten-Inventur 2026-07: JA-Werte (Drive-PDFs) jetzt strukturiert in DB.

import type { JaEintrag, JaResponse } from "../../../lib/api";
import { FIRMA_LABEL, firmaBorder } from "../../../lib/firma-colors";
import { useCountUp } from "../../../hooks/useCountUp";

function eur(v: string | null | undefined): number | null {
  if (v == null) return null;
  const n = Number.parseFloat(v);
  return Number.isFinite(n) ? n : null;
}

function fmt(n: number | null): string {
  if (n == null) return "—";
  return `${Math.round(n).toLocaleString("de-DE")} €`;
}

function FirmaRow({ aktuell, vorjahr }: { aktuell: JaEintrag; vorjahr?: JaEintrag }) {
  const jue = useCountUp(eur(aktuell.jahresueberschuss_eur));
  const ek = eur(aktuell.eigenkapital_eur);
  const ekVj = vorjahr ? eur(vorjahr.eigenkapital_eur) : null;
  const jueVj = vorjahr ? eur(vorjahr.jahresueberschuss_eur) : null;
  const jueNow = eur(aktuell.jahresueberschuss_eur);
  const delta =
    jueNow != null && jueVj != null && jueVj !== 0
      ? Math.round(((jueNow - jueVj) / Math.abs(jueVj)) * 100)
      : null;

  return (
    <div className={`flex items-center gap-3 rounded-xl border-l-2 bg-slate-900/40 px-3 py-2 ${firmaBorder(aktuell.firma_id)}`}>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-slate-100">
          {(FIRMA_LABEL as Record<string, string>)[aktuell.firma_id] ?? aktuell.firma_id}
          <span className="ml-2 text-[11px] font-normal text-slate-500">JA {aktuell.jahr}</span>
        </p>
        <p className="truncate text-[11px] text-slate-400">
          EK {fmt(ek)}
          {aktuell.ek_quote_pct != null && (
            <span className="text-slate-500"> · {aktuell.ek_quote_pct} % Quote</span>
          )}
          {ekVj != null && ek != null && (
            <span className={ek >= ekVj ? "text-emerald-400" : "text-pink-400"}>
              {" "}
              {ek >= ekVj ? "▲" : "▼"}
            </span>
          )}
        </p>
      </div>
      {(() => {
        const u = eur(aktuell.umsatzerloese_eur);
        const uVj = vorjahr ? eur(vorjahr.umsatzerloese_eur) : null;
        if (u == null || uVj == null) return null;
        const max = Math.max(u, uVj, 1);
        return (
          <div className="hidden w-16 shrink-0 flex-col justify-center gap-1 sm:flex" aria-hidden>
            <div className="h-1.5 rounded-full bg-slate-600/80" style={{ width: `${Math.max(6, (uVj / max) * 100)}%` }} />
            <div className="h-1.5 rounded-full bg-cyan-400/80" style={{ width: `${Math.max(6, (u / max) * 100)}%` }} />
          </div>
        );
      })()}
      <div className="shrink-0 text-right">
        <p
          className={`font-mono text-sm font-semibold ${
            (jueNow ?? 0) >= 0 ? "text-emerald-300" : "text-pink-300"
          }`}
        >
          {fmt(jue)}
        </p>
        <p className="text-[10px] uppercase tracking-wider text-slate-500">
          JÜ{delta != null && ` · ${delta > 0 ? "+" : ""}${delta} % vs. VJ`}
        </p>
      </div>
    </div>
  );
}

export default function JaKompassBentoTile({
  data,
  loading,
}: {
  data?: JaResponse | null;
  loading?: boolean;
}) {
  const eintraege = data?.eintraege ?? [];
  const jahre = [...new Set(eintraege.map((e) => e.jahr))].sort((a, b) => b - a);
  const aktuellesJahr = jahre[0];
  const aktuelle = eintraege.filter((e) => e.jahr === aktuellesJahr);
  const byFirmaJahr = new Map(eintraege.map((e) => [`${e.firma_id}:${e.jahr}`, e]));

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-baseline justify-between">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-400">
          JA-Kompass · festgestellte Abschlüsse
        </h2>
        {aktuellesJahr && <span className="text-[11px] text-slate-500">{aktuellesJahr}</span>}
      </div>

      {loading && eintraege.length === 0 && (
        <div className="mt-3 space-y-2">
          <div className="h-10 animate-pulse rounded-xl bg-slate-800/60" />
          <div className="h-10 animate-pulse rounded-xl bg-slate-800/60" />
        </div>
      )}

      {!loading && eintraege.length === 0 && (
        <p className="mt-3 text-sm text-slate-500">
          {data?.message ?? "Noch keine JA-Daten — Seed folgt beim nächsten Deploy."}
        </p>
      )}

      <div className="mt-2 space-y-1.5 overflow-hidden">
        {aktuelle.map((e) => (
          <FirmaRow
            key={`${e.firma_id}-${e.jahr}`}
            aktuell={e}
            vorjahr={byFirmaJahr.get(`${e.firma_id}:${e.jahr - 1}`)}
          />
        ))}
      </div>
      {eintraege.length > 0 && (
        <p className="mt-auto pt-1 text-[10px] text-slate-600">
          Quelle: festgestellte Jahresabschlüsse (Drive) · G+K folgt
        </p>
      )}
    </div>
  );
}
